/**
 * One control for any registry entry, mapping `entry.type` → input element.
 * Used by Task 5's CategoryEditor to render every editable setting uniformly.
 *
 * `onChange` fires with the PARSED value (a number, boolean, string, string[],
 * or object) — never the raw control text — so callers store the same shape the
 * console API expects. An inline error line surfaces `clientValidate` (the
 * client mirror of the server's authoritative validator) plus, for the
 * text-parsed types (list / json / table), any parse failure on the raw input.
 *
 * Styling is deferred to Task 8; only semantic classNames are emitted here.
 */

import { useEffect, useRef, useState } from 'react';
import { assertNever, clientValidate, type SettingEntry } from '../lib/registry';

export interface SettingControlProps {
  entry: SettingEntry;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
}

/** Pretty-print an array (list) as one item per line. */
function listToText(value: unknown): string {
  if (Array.isArray(value)) return value.map((v) => String(v)).join('\n');
  return '';
}

/** Pretty-print an object/array (json / table) as indented JSON. */
function jsonToText(value: unknown): string {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

export default function SettingControl({
  entry,
  value,
  onChange,
  disabled,
}: SettingControlProps) {
  // For text-parsed types the textarea holds raw text that may not yet parse;
  // a local parse error takes precedence over clientValidate.
  const [parseError, setParseError] = useState<string | null>(null);
  // Re-sync the raw text when the incoming value changes for text-parsed types.
  const isList = entry.type === 'list';
  const isJson = entry.type === 'json' || entry.type === 'table';
  const [text, setText] = useState<string>(() =>
    isList ? listToText(value) : isJson ? jsonToText(value) : '',
  );
  // Track the exact value this component last emitted via onChange. When the
  // parent feeds that same value straight back (controlled usage), the incoming
  // `value` is reference-identical to what we emitted, so we must NOT re-derive
  // the textarea text — doing so would clobber the in-progress edit (a list's
  // trailing newline deleted, JSON reformatted with the cursor yanked). We only
  // resync for genuinely EXTERNAL value changes (e.g. a parent reset after save).
  const lastEmitted = useRef<unknown>(value);
  useEffect(() => {
    if (Object.is(value, lastEmitted.current)) return;
    if (isList) setText(listToText(value));
    else if (isJson) setText(jsonToText(value));
    // Resync only on external value changes or an entry.key change.
  }, [value, isList, isJson, entry.key]);

  /** Emit a parsed value and remember it so its echo doesn't trigger a resync. */
  function emit(next: unknown) {
    lastEmitted.current = next;
    onChange(next);
  }

  const validationError = parseError ?? clientValidate(entry, value);
  const errorId = `${entry.key}-error`;
  const describedBy = validationError ? errorId : undefined;

  let control: React.ReactNode;
  switch (entry.type) {
    case 'number':
    case 'integer': {
      const c = entry.constraints ?? {};
      control = (
        <span className="control control-number">
          <input
            type="number"
            className="input"
            disabled={disabled}
            value={typeof value === 'number' ? value : ''}
            min={typeof c.min === 'number' ? c.min : undefined}
            max={typeof c.max === 'number' ? c.max : undefined}
            step={entry.type === 'integer' ? 1 : 'any'}
            aria-label={entry.title}
            aria-describedby={describedBy}
            onChange={(e) => {
              const raw = e.target.value;
              emit(raw === '' ? undefined : Number(raw));
            }}
          />
          {entry.unit && <span className="unit">{entry.unit}</span>}
        </span>
      );
      break;
    }
    case 'boolean':
      control = (
        <input
          type="checkbox"
          className="input control-boolean"
          disabled={disabled}
          checked={value === true}
          aria-label={entry.title}
          aria-describedby={describedBy}
          onChange={(e) => emit(e.target.checked)}
        />
      );
      break;
    case 'enum': {
      const values = (entry.constraints?.values as unknown[]) ?? [];
      control = (
        <select
          className="input control-enum"
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          aria-label={entry.title}
          aria-describedby={describedBy}
          onChange={(e) => emit(e.target.value)}
        >
          {/* placeholder when the current value isn't a known option */}
          {!values.includes(value) && <option value="">—</option>}
          {values.map((v) => (
            <option key={String(v)} value={String(v)}>
              {String(v)}
            </option>
          ))}
        </select>
      );
      break;
    }
    case 'string':
      control = (
        <input
          type="text"
          className="input control-string"
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          aria-label={entry.title}
          aria-describedby={describedBy}
          onChange={(e) => emit(e.target.value)}
        />
      );
      break;
    case 'list':
      control = (
        <textarea
          className="input control-list"
          disabled={disabled}
          value={text}
          aria-label={entry.title}
          aria-describedby={describedBy}
          placeholder="one item per line"
          onChange={(e) => {
            const next = e.target.value;
            setText(next);
            setParseError(null);
            const items = next
              .split('\n')
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            emit(items);
          }}
        />
      );
      break;
    case 'json':
    case 'table':
      control = (
        <textarea
          className="input control-json mono"
          disabled={disabled}
          value={text}
          aria-label={entry.title}
          aria-describedby={describedBy}
          placeholder={entry.type === 'table' ? '[ ... ] or { ... }' : '{ ... }'}
          onChange={(e) => {
            const next = e.target.value;
            setText(next);
            if (next.trim() === '') {
              setParseError(null);
              emit(undefined);
              return;
            }
            try {
              const parsed: unknown = JSON.parse(next);
              setParseError(null);
              emit(parsed);
            } catch {
              setParseError(`${entry.key}: invalid JSON`);
            }
          }}
        />
      );
      break;
    case 'markdown':
      control = (
        <textarea
          className="input control-markdown mono"
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          aria-label={entry.title}
          aria-describedby={describedBy}
          onChange={(e) => emit(e.target.value)}
        />
      );
      break;
    default:
      control = assertNever(entry.type);
  }

  return (
    <div className="setting-control" data-type={entry.type}>
      {control}
      {validationError && (
        <p id={errorId} className="error" role="alert">
          {validationError}
        </p>
      )}
    </div>
  );
}
